<div class="relative">
  @if (isLoadingData()) {
    <div class="space-y-5 animate-pulse" role="status" aria-label="Cargando producto">
      <div class="h-11 w-full rounded-lg bg-gray-200 dark:bg-gray-800"></div>
      <div class="h-16 w-full rounded-lg bg-gray-100 dark:bg-gray-800/70"></div>
      <div class="grid grid-cols-2 gap-4">
        <div class="h-11 rounded-lg bg-gray-200 dark:bg-gray-800"></div>
        <div class="h-11 rounded-lg bg-gray-200 dark:bg-gray-800"></div>
      </div>
      <div class="h-24 w-full rounded-xl bg-gray-100 dark:bg-gray-800/70"></div>
    </div>
  } @else {
    <form [formGroup]="productForm" class="pa" novalidate>

      <!-- Basics -->
      <section class="pa-section" aria-labelledby="pa-basics">
        <h3 id="pa-basics" class="pa-section__title">Qué vendes</h3>

        <div>
          <label for="name" class="pa-label">Nombre <span class="pa-req" aria-hidden="true">*</span></label>
          <input type="text" id="name" formControlName="name" placeholder="Ej: Shampoo hidratante 500 ml"
            class="pa-input" [class.pa-input--error]="showError('name')"
            aria-required="true" [attr.aria-invalid]="showError('name')"
            [attr.aria-describedby]="showError('name') ? 'name-error' : null" />
          @if (showError('name')) {
            <p id="name-error" class="pa-error" role="alert">
              @if (productForm.get('name')?.errors?.['required']) {
                Escribe el nombre del producto.
              } @else {
                El nombre debe tener al menos 3 caracteres.
              }
            </p>
          }
        </div>

        <div>
          <label for="description" class="pa-label">Descripción <span class="pa-optional">opcional</span></label>
          <textarea id="description" formControlName="description" rows="2"
            placeholder="Presentación, tamaño o uso recomendado"
            class="pa-input pa-input--area"></textarea>
        </div>

        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label for="sku" class="pa-label">SKU o código <span class="pa-optional">opcional</span></label>
            <div class="relative">
              <input type="text" id="sku" formControlName="sku" placeholder="Ej: SHP-001"
                class="pa-input" [class.pa-input--action]="!productForm.get('sku')?.value" />
              @if (!productForm.get('sku')?.value) {
                <button type="button" (click)="generateSku()" class="pa-inline-btn">Generar</button>
              }
            </div>
          </div>
          <div>
            <label for="category" class="pa-label">Categoría <span class="pa-optional">opcional</span></label>
            <input type="text" id="category" formControlName="category" placeholder="Ej: Cuidado capilar"
              class="pa-input" />
          </div>
        </div>
      </section>

      <!-- Price -->
      <section class="pa-section" aria-labelledby="pa-price">
        <h3 id="pa-price" class="pa-section__title">Precio</h3>

        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label for="cost" class="pa-label">Te cuesta <span class="pa-req" aria-hidden="true">*</span></label>
            <div class="relative">
              <span class="pa-prefix" aria-hidden="true">$</span>
              <input type="text" inputmode="numeric" id="cost" formControlName="cost" placeholder="0"
                autocomplete="off" (input)="onCostInput($event)"
                class="pa-input pa-input--money" [class.pa-input--error]="showError('cost')"
                aria-required="true" [attr.aria-invalid]="showError('cost')"
                [attr.aria-describedby]="showError('cost') ? 'cost-error' : null" />
            </div>
            @if (showError('cost')) {
              <p id="cost-error" class="pa-error" role="alert">Escribe cuánto pagas por cada unidad.</p>
            }
          </div>

          <div>
            <label for="price" class="pa-label">Lo vendes a <span class="pa-req" aria-hidden="true">*</span></label>
            <div class="relative">
              <span class="pa-prefix" aria-hidden="true">$</span>
              <input type="text" inputmode="numeric" id="price" formControlName="price" placeholder="0"
                autocomplete="off" (input)="onPriceInput($event)"
                class="pa-input pa-input--money" [class.pa-input--error]="showError('price')"
                aria-required="true" [attr.aria-invalid]="showError('price')"
                [attr.aria-describedby]="showError('price') ? 'price-error' : null" />
            </div>
            @if (showError('price')) {
              <p id="price-error" class="pa-error" role="alert">Escribe el precio de venta al cliente.</p>
            }
          </div>
        </div>

        <!-- Split of the sale price into cost and profit -->
        @if (profitMargin() !== null) {
          <div class="pa-split" [attr.data-state]="profitAmount() > 0 ? 'gain' : profitAmount() === 0 ? 'even' : 'loss'"
            aria-live="polite">
            <div class="pa-split__bar" aria-hidden="true">
              <span class="pa-split__cost" [style.width.%]="costShare()"></span>
              <span class="pa-split__gain"></span>
            </div>
            <p class="pa-split__text">
              @if (profitAmount() > 0) {
                Ganas <strong>{{ formatCurrency(profitAmount()) }}</strong> por unidad, un margen de {{ profitMargin()! | number:'1.0-1' }} % sobre el costo.
              } @else if (profitAmount() === 0) {
                Vendes al costo: no ganas nada por unidad.
              } @else {
                Pierdes <strong>{{ formatCurrency(-profitAmount()) }}</strong> por unidad. Revisa el precio de venta.
              }
            </p>
          </div>
        }
      </section>

      <!-- Stock -->
      <section class="pa-section" aria-labelledby="pa-stock">
        <h3 id="pa-stock" class="pa-section__title">Inventario</h3>

        <div class="grid grid-cols-1 items-start gap-4 sm:grid-cols-2">
          <div>
            <label for="stock" class="pa-label">Unidades disponibles <span class="pa-req" aria-hidden="true">*</span></label>
            <input type="number" min="0" inputmode="numeric" id="stock" formControlName="stock" placeholder="0"
              class="pa-input" [class.pa-input--error]="showError('stock')"
              aria-required="true" [attr.aria-invalid]="showError('stock')"
              [attr.aria-describedby]="showError('stock') ? 'stock-error' : null" />
            @if (showError('stock')) {
              <p id="stock-error" class="pa-error" role="alert">
                @if (productForm.get('stock')?.errors?.['min']) {
                  Las unidades no pueden ser negativas.
                } @else {
                  Escribe cuántas unidades tienes, aunque sea 0.
                }
              </p>
            }
          </div>

          <div class="pa-toggle">
            <div class="min-w-0">
              <p id="pa-active-label" class="pa-toggle__title">Disponible para vender</p>
              <p class="pa-toggle__hint">
                {{ productForm.get('isActive')?.value ? 'Aparece al registrar ventas.' : 'Oculto al registrar ventas.' }}
              </p>
            </div>
            <button type="button" (click)="toggleActive()" class="pa-switch"
              role="switch" [attr.aria-checked]="!!productForm.get('isActive')?.value" aria-labelledby="pa-active-label">
              <span class="pa-switch__knob" aria-hidden="true"></span>
            </button>
          </div>
        </div>
      </section>
    </form>
  }
</div>
